import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";

interface LoginFormProps {
  onSuccess?: () => void;
  onRegister?: () => void;
  onForgotPassword?: () => void;
}

export default function LoginForm({ onSuccess, onRegister, onForgotPassword }: LoginFormProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { signInWithEmail } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();

  const validateForm = () => {
    if (!email || !password) {
      toast({
        title: "Vul alle velden in",
        description: "Alle velden zijn verplicht.",
        variant: "destructive",
      });
      return false;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast({
        title: "Ongeldig e-mailadres",
        description: "Voer een geldig e-mailadres in.",
        variant: "destructive",
      });
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    console.log('Attempting login with email:', email);

    try {
      await signInWithEmail(email, password);
      console.log('Login successful');
      toast({
        title: "Succesvol ingelogd",
        description: "Welkom terug bij Turnles Planner!",
      });
      navigate('/');
      onSuccess?.();
    } catch (error: any) {
      console.error('Login error:', error);
      // Error messages worden al getoond door useAuthActions
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <Input
            type="email"
            placeholder="E-mailadres"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={loading}
            required
          />
        </div>
        <div>
          <Input
            type="password"
            placeholder="Wachtwoord"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={loading}
            required
          />
        </div>
        <Button type="submit" className="w-full" disabled={loading}>
          {loading ? "Bezig met inloggen..." : "Inloggen"}
        </Button>
      </form>

      <div className="flex justify-center">
        <Button
          variant="link"
          className="text-sm text-muted-foreground"
          onClick={onForgotPassword}
          disabled={loading}
        >
          Wachtwoord vergeten?
        </Button>
      </div>

      {onRegister && (
        <div className="text-center">
          <Button
            variant="link"
            className="text-sm text-muted-foreground"
            onClick={onRegister}
            disabled={loading}
          >
            Nog geen account? Registreren
          </Button>
        </div>
      )}
    </div>
  );
}
